import React from "react";

/** Kebab-case ASCII slug; shared by heading anchors and the search index. */
export function slugify(text) {
  return String(text)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Flat heading index (level, text, slug) from markdown, skipping code fences. */
export function extractHeadings(md) {
  if (!md) return [];
  const out = [];
  let inCodeFence = false;
  for (const line of md.split("\n")) {
    if (line.startsWith("```")) {
      inCodeFence = !inCodeFence;
    } else if (!inCodeFence) {
      const m = /^(#{1,4})\s+(.+?)\s*$/.exec(line);
      if (m) {
        const text = m[2].replace(/[#`*_]/g, "").trim();
        out.push({ level: m[1].length, text, slug: slugify(text) });
      }
    }
  }
  return out;
}

/** Splits text by the query and wraps matches in <mark class="help-hl">. */
export function makeHighlighter(query) {
  if (!query) return (t) => t;
  const re = new RegExp(`(${escapeRegex(query)})`, "gi");
  const q = query.toLowerCase();
  return (t) => {
    if (typeof t !== "string") return t;
    return t.split(re).map((part, i) => (part.toLowerCase() === q
      ? <mark key={`m-${i}-${part}`} className="help-hl">{part}</mark>
      : <React.Fragment key={`t-${i}-${part.length}`}>{part}</React.Fragment>));
  };
}

function HeadingRenderer({ level, children, ...rest }) {
  const Tag = `h${level}`;
  const text = React.Children.toArray(children)
    .map((c) => (typeof c === "string" ? c : c?.props?.children ?? ""))
    .join("");
  const slug = slugify(text);
  return <Tag id={slug} data-heading-slug={slug} {...rest}>{children}</Tag>;
}

const HIGHLIGHTED_TAGS = ["p", "li", "td", "th", "strong", "em"];

/** react-markdown component map: anchored headings + highlighted text nodes. */
export function buildMarkdownComponents(highlighter) {
  const wrap = (Tag) => function Highlighted({ children, node, ...rest }) {
    return <Tag {...rest}>{React.Children.map(children, (c) => (typeof c === "string" ? highlighter(c) : c))}</Tag>;
  };
  const headings = Object.fromEntries([1, 2, 3, 4].map((lvl) => [
    `h${lvl}`, function Heading({ node, ...props }) { return <HeadingRenderer level={lvl} {...props} />; },
  ]));
  return { ...headings, ...Object.fromEntries(HIGHLIGHTED_TAGS.map((t) => [t, wrap(t)])) };
}
